import React, { useState, useRef, useEffect, useCallback } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import _ from 'lodash';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { sendSimulatedMessage, getSimulatorThread } from '../../api/concierge_simulator';
import { fetchUserThroughUsername } from '../../api/users';
import { updateConciergeThread, getConciergeThreadsForAdmin, testWelcomeMessage } from '../../api/noir';
import Select from 'react-select';
import { getSmartImage } from '../../helpers/helpers';
import { getMessageImages } from '../../helpers/concierge_helpers';
import './NoirSimulator.scss';

const threadSelectStyles = {
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? '#dcf6ea' : isFocused ? '#dcf6ea' : 'white',
    color: '#171919',
    cursor: 'pointer',
    padding: '10px 12px'
  }),
  control: base => ({
    ...base,
    minHeight: 38,
    borderColor: '#e5e5e5',
    '&:hover': { borderColor: '#11835a' }
  }),
  menu: base => ({
    ...base,
    zIndex: 20,
    borderRadius: 6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
  }),
  menuList: base => ({
    ...base,
    padding: 4
  })
};

const NoirSimulator = () => {
  const history = useHistory();
  const location = useLocation();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const messagesEndRef = useRef(null);
  const [allThreads, setAllThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [welcomePreview, setWelcomePreview] = useState(null);
  const [welcomeLoading, setWelcomeLoading] = useState(false);

  const threadOptions = allThreads.map(t => ({
    value: t,
    label: `${t.smsPhoneNumber || '-'} - ${t.User_name || '-'} - ${t.User_username || '-'}`
  }));

  const params = new URLSearchParams(location.search);
  const urlThreadId = params.get('thread');

  const conciergeThread = allThreads.find(t => String(t.id) === urlThreadId);

  const setUrlThreadId = id => {
    const next = new URLSearchParams(location.search);
    if (id) next.set('thread', id);
    else next.delete('thread');
    history.replace({ search: next.toString() });
  };

  const formatThreadOption = (option, { context }) => {
    const t = option?.value;
    if (!t) return option.label;
    if (context === 'value') return t.smsPhoneNumber || '—';
    const name = t.User_name || '—';
    const phone = t.smsPhoneNumber || '—';
    const username = t.User_username || '—';
    const createdAgo = t.createdAt ? moment(t.createdAt).fromNow() : '—';
    return (
      <div className='thread-option'>
        <div className='thread-option__header'>
          {name} | {phone}
        </div>
        <div className='thread-option__username'>@{username}</div>
        <div className='thread-option__meta'>Created {createdAgo}</div>
      </div>
    );
  };

  useEffect(() => {
    const fetchAllThreads = async () => {
      const data = await getConciergeThreadsForAdmin();
      setAllThreads(data.threads || []);
    };
    fetchAllThreads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (urlThreadId && !selectedThread) {
      const match = allThreads.find(t => String(t.id) === urlThreadId);
      if (match) {
        setSelectedThread({ value: match, label: `${match.smsPhoneNumber || '-'} - ${match.User_name || '-'} - ${match.User_username || '-'}` });
        setPhoneNumber(match.smsPhoneNumber || '');
      }
    }
  }, [urlThreadId, allThreads, selectedThread]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending]);

  const refreshMessages = useCallback(async () => {
    if (!phoneNumber || phoneNumber.length < 10) return;
    setLoadingThread(true);
    try {
      const data = await getSimulatorThread(phoneNumber);
      if (data.thread) {
        setAllThreads(prev => [data.thread, ...prev.filter(t => t.id !== data.thread.id)]);
      }

      if (data.messages) {
        setMessages(
          data.messages.map(m => ({
            id: m.id,
            content: m.content,
            senderType: m.senderType,
            createdAt: m.createdAt,
            media: m.media
          }))
        );
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
    } finally {
      setLoadingThread(false);
    }
  }, [phoneNumber]);

  // Debounced thread loader
  const loadThread = useCallback(_.debounce(refreshMessages, 500), [refreshMessages]);

  useEffect(() => {
    loadThread(phoneNumber);
  }, [phoneNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const addThreadToAllThreads = thread => {
    if (thread) {
      setAllThreads(prev => [thread, ...prev.filter(t => t.id !== thread.id)]);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || !phoneNumber.trim() || sending) return;

    const text = inputValue.trim();
    setInputValue('');

    // Optimistic: show user bubble immediately
    const tempId = `temp-${Date.now()}`;
    setMessages(prev => [...prev, { id: tempId, content: text, senderType: 'user', createdAt: new Date().toISOString() }]);
    setSending(true);

    try {
      const data = await sendSimulatedMessage({ message: text, phoneNumber: phoneNumber.trim() });
      if (data.thread) {
        addThreadToAllThreads(data.thread);
        setUrlThreadId(data.thread.id);
      }
      // Replace temp user message with real one and add AI response
      setMessages(prev => {
        const updated = prev.map(m => (m.id === tempId && data.userMessage ? { ...data.userMessage, senderType: 'user' } : m));
        if (data.aiMessage) {
          updated.push({ ...data.aiMessage, senderType: 'ai' });
        }
        return updated;
      });
    } catch (err) {
      cogoToast.error(err || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewShopper = async () => {
    const input = window.prompt('Phone number:');
    if (!input?.trim()) return;
    const phone = input.trim();
    if (phone.length < 10) {
      cogoToast.error('Please enter a valid phone number (e.g. +15551234567)');
      return;
    }
    try {
      const data = await getSimulatorThread(phone);
      const thread = data.thread;
      if (!data.success) throw new Error('No thread returned');
      // No thread returned? Then it's a new thread
      if (thread) {
        addThreadToAllThreads(thread);
        setUrlThreadId(thread.id);
      }
      setPhoneNumber(thread?.smsPhoneNumber || phone);
      cogoToast.success('Thread ready');
    } catch (err) {
      cogoToast.error(err?.message || 'Failed to find or create thread');
    }
  };

  const handleSimulateCreatedAccount = async () => {
    const userName = window.prompt('Enter a username to attach this thread to:');
    if (!userName) return;
    const data = await fetchUserThroughUsername(userName);
    if (!data.user?.id) return cogoToast.error('User not found');
    if (!conciergeThread) return cogoToast.error('No thread found');
    try {
      await updateConciergeThread(conciergeThread.id, { User_id: data.user.id });
      cogoToast.success('Thread linked to user');
      refreshMessages();
    } catch (err) {
      cogoToast.error(err?.message || 'Failed to link thread');
    }
  };

  return (
    <div className='noir-simulator'>
      <div className='simulator-phone-input simulator-phone-input--wide'>
        <label>Threads</label>
        <div className='thread-select-row'>
          <Select
            classNamePrefix='thread-select'
            value={selectedThread}
            onChange={option => {
              if (option?.value) {
                setUrlThreadId(option.value.id);
                if (option.value.smsPhoneNumber) setPhoneNumber(option.value.smsPhoneNumber);
              } else {
                setUrlThreadId(null);
                setPhoneNumber('');
              }
            }}
            options={threadOptions}
            formatOptionLabel={formatThreadOption}
            placeholder='Select a thread...'
            isClearable
            styles={threadSelectStyles}
          />
          <button type='button' className='thread-select-new' onClick={handleNewShopper} title='New shopper'>
            + New shopper
          </button>
        </div>
      </div>
      <div className='simulator-phone-frame'>
        <div className='phone-notch' />
        <div className='phone-header'>
          <span className='phone-header-title'>{phoneNumber || 'Noir Concierge'}</span>
          {phoneNumber && phoneNumber.length >= 10 && (
            <button
              type='button'
              className={cn('phone-header-refresh', { spinning: loadingThread })}
              onClick={refreshMessages}
              disabled={loadingThread}
              title='Refresh messages'
            >
              ↻
            </button>
          )}
        </div>
        <div className='phone-messages'>
          {loadingThread && <div className='phone-loading'>Loading conversation...</div>}
          {!loadingThread && messages.length === 0 && (
            <div className='phone-empty'>
              {phoneNumber ? 'No messages yet. Send one below.' : 'Enter a phone number to start.'}
              <button
                type='button'
                className='phone-button simulate-welcome-btn'
                disabled={welcomeLoading}
                onClick={async () => {
                  setWelcomeLoading(true);
                  try {
                    const result = await testWelcomeMessage('just_signed_up');
                    setWelcomePreview(result.message);
                  } catch (err) {
                    cogoToast.error(err || 'Failed to generate welcome preview');
                  } finally {
                    setWelcomeLoading(false);
                  }
                }}
              >
                {welcomeLoading ? 'Generating...' : welcomePreview ? 'Regenerate' : 'Simulate New Shopper Clicking "Text Me"'}
              </button>
              {welcomePreview && <div className='phone-bubble bubble-ai welcome-preview-bubble'>{welcomePreview}</div>}
            </div>
          )}
          {messages.map(msg => {
            const images = getMessageImages(msg.media);
            return (
            <React.Fragment key={msg.id}>
              <div className={cn('phone-bubble', msg.senderType === 'user' ? 'bubble-user' : 'bubble-ai')}>
                {images.map((m, idx) => (
                  <img key={idx} src={getSmartImage(m.mediaUrl)} alt={m.description || 'Attached image'} className='bubble-image' />
                ))}
                {msg.content}
              </div>
              {msg.content?.includes('/setup/noir') && (
                <button type='button' className='phone-button' onClick={handleSimulateCreatedAccount}>
                  Simulate created account
                </button>
              )}
            </React.Fragment>
          );
          })}
          {sending && (
            <div className='phone-bubble bubble-ai bubble-typing'>
              <span className='dot' />
              <span className='dot' />
              <span className='dot' />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className='phone-input-bar'>
          <input
            type='text'
            placeholder='Type a message...'
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending || !phoneNumber.trim()}
          />
          <button onClick={handleSend} disabled={!inputValue.trim() || sending || !phoneNumber.trim()}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoirSimulator;
